<!--
  THE INBOX BELL (carrier workflows, decision D-04), beside the vehicle alert
  bell. Scheduling decisions, conflict alerts, published statements and import
  results land in `notifications`; this shows the unread count and the newest
  rows, and marks them read. Same shape and the same reasoning as alertBell:
  a bounded poll on a slow timer rather than a second EventSource per tab, a
  popover that keeps LwMenu's contract (Escape and outside click close, focus
  returns to the trigger).
-->
<template>
  <div ref="root" class="lw-p-notice-bell">
    <button
      ref="trigger"
      type="button"
      class="lw-p-notice-bell__trigger"
      :aria-expanded="String(open)"
      aria-controls="lw-notice-panel"
      :aria-label="triggerLabel"
      @click="toggle"
    >
      <i class="fa fa-inbox" aria-hidden="true" />
      <span v-if="badgeCount > 0" class="lw-p-notice-bell__badge" aria-hidden="true">
        {{ badgeCount > 9 ? '9+' : badgeCount }}
      </span>
    </button>

    <div
      v-if="open"
      id="lw-notice-panel"
      class="lw-p-notice-bell__panel"
      role="group"
      aria-label="Notifications"
    >
      <div class="lw-p-notice-bell__panel-head">
        <h2 class="lw-p-notice-bell__panel-title">Notifications</h2>
        <LwButton v-if="unread > 0" variant="link" size="sm" @click="readAll">
          Mark all read
        </LwButton>
        <button
          type="button"
          class="lw-p-notice-bell__close"
          aria-label="Close notifications"
          @click="close"
        >
          <i class="fa fa-times" aria-hidden="true" />
        </button>
      </div>

      <!--
        Spec 39: unread message threads ride the bell's badge, and get one
        row here that opens the inbox rather than a row per thread (the
        inbox is the list; this only says there is something in it).
      -->
      <div v-if="messageThreads > 0" class="lw-p-notice-bell__messages" data-test="bell-messages">
        <p class="lw-p-notice-bell__subject">{{ messagesLine }}</p>
        <LwButton v-if="inboxRoute" variant="link" size="sm" @click="openInbox">Open</LwButton>
      </div>

      <p v-if="loading" class="lw-p-notice-bell__status" aria-busy="true">Loading…</p>
      <p v-else-if="failed" class="lw-p-notice-bell__status">
        Notifications could not be loaded.
        <LwButton variant="link" size="sm" @click="fetchRows">Retry</LwButton>
      </p>
      <div v-else-if="rows.length === 0" class="lw-p-notice-bell__empty">
        <p class="lw-p-notice-bell__empty-title">Nothing new</p>
        <p class="lw-p-notice-bell__status">
          Time-off decisions, scheduling conflicts and statement updates appear here.
        </p>
      </div>
      <ul v-else class="lw-p-notice-bell__list">
        <li
          v-for="row in rows"
          :key="row.id"
          class="lw-p-notice-bell__item"
          :class="{ 'lw-p-notice-bell__item--unread': !row.read_at }"
        >
          <p class="lw-p-notice-bell__subject">{{ row.title }}</p>
          <p v-if="row.body" class="lw-p-notice-bell__body">{{ row.body }}</p>
          <p class="lw-p-notice-bell__meta">{{ whenOf(row) }}</p>
          <div class="lw-p-notice-bell__actions">
            <LwButton v-if="linkFor(row)" variant="link" size="sm" @click="openLink(row)">
              Open
            </LwButton>
            <LwButton v-if="!row.read_at" variant="link" size="sm" @click="markRead(row)">
              Mark read
            </LwButton>
          </div>
        </li>
      </ul>
    </div>
  </div>
</template>

<script>
import LwButton from '../lw/LwButton.vue';
import { ageLabel } from '../../pages/Tracking/lib/time.js';

const POLL_MS = 60000;

// Where a notice's subject lives in the SPA. Unknown subjects have no link.
// Each builder takes the subject id and the whole row: spec 52's charge and
// stop notices are addressed by the charge or stop id, but the page that
// shows them is the LOAD's, whose id rides in `data.load_id`. A row without
// it has nowhere to go and gets no link.
const loadTab = (row, tab) =>
  row && row.data && row.data.load_id
    ? { name: 'LoadsView', params: { id: row.data.load_id }, query: { tab } }
    : null;
const SUBJECT_ROUTES = {
  load: id => ({ name: 'LoadsView', params: { id } }),
  driver: id => ({ name: 'DriverEdit', params: { id } }),
  settlement: id => ({ name: 'SettlementDetail', params: { id } }),
  time_off: () => ({ name: 'Scheduling', query: { tab: 'requests' } }),
  scheduling_conflict: () => ({ name: 'Scheduling', query: { tab: 'conflicts' } }),
  rate_confirmation: id => ({ name: 'ImportLoad', params: { id } }),
  // Spec 52 section 5: a charge opens the load's Stops & charges tab; a stop
  // (a notice reminder, a driver confirmation) opens the load.
  accessorial_charge: (id, row) => loadTab(row, 'stops-charges'),
  stop: (id, row) => loadTab(row, 'stops-charges'),
};

export default {
  name: 'NoticeBell',
  components: { LwButton },
  data() {
    return {
      open: false,
      loading: false,
      failed: false,
      rows: [],
      unread: 0,
      // Spec 39: GET /messages/unread's `threads`, on the same timer.
      messageThreads: 0,
      now: Date.now(),
      timer: null,
    };
  },
  computed: {
    badgeCount() {
      return this.unread + this.messageThreads;
    },
    triggerLabel() {
      if (this.badgeCount === 0) return 'Notifications, none unread';
      const parts = [`Notifications, ${this.unread} unread`];
      if (this.messageThreads > 0) parts.push(this.threadsPhrase(this.messageThreads));
      return parts.join(', ');
    },
    messagesLine() {
      return this.threadsPhrase(this.messageThreads);
    },
    inboxRoute() {
      const route = { name: 'MessagesInbox' };
      const known = this.$router && this.$router.hasRoute && this.$router.hasRoute(route.name);
      return known ? route : null;
    },
  },
  created() {
    this.fetchRows();
    this.fetchMessageCount();
    this.timer = setInterval(() => {
      this.fetchRows();
      this.fetchMessageCount();
    }, POLL_MS);
  },
  mounted() {
    document.addEventListener('keydown', this.onKeydown);
    document.addEventListener('mousedown', this.onPointer);
  },
  beforeUnmount() {
    if (this.timer) clearInterval(this.timer);
    document.removeEventListener('keydown', this.onKeydown);
    document.removeEventListener('mousedown', this.onPointer);
  },
  methods: {
    async fetchRows() {
      this.loading = this.rows.length === 0;
      try {
        const { data } = await this.$http.get('/notifications?limit=20');
        this.rows = Array.isArray(data.rows) ? data.rows : [];
        this.unread = Number(data.unread) || 0;
        this.failed = false;
        this.now = Date.now();
      } catch (_e) {
        this.failed = true;
      } finally {
        this.loading = false;
      }
    },
    // Spec 39: its own request on the same timer, and never the bell's
    // failure: notices that loaded are not "could not be loaded" because the
    // message count did not. A failure counts as none.
    async fetchMessageCount() {
      try {
        const { data } = await this.$http.get('/messages/unread');
        this.messageThreads = Math.max(0, Number(data && data.threads) || 0);
      } catch (_e) {
        this.messageThreads = 0;
      }
    },
    threadsPhrase(n) {
      return n === 1 ? '1 unread message thread' : `${n} unread message threads`;
    },
    openInbox() {
      if (!this.inboxRoute) return;
      this.close();
      this.$router.push(this.inboxRoute);
    },
    async markRead(row) {
      try {
        await this.$http.post(`/notifications/${row.id}/read`);
        row.read_at = new Date().toISOString();
        this.unread = Math.max(0, this.unread - 1);
      } catch (_e) {
        this.failed = true;
      }
    },
    async readAll() {
      try {
        await this.$http.post('/notifications/read-all');
        const at = new Date().toISOString();
        this.rows.forEach(r => {
          if (!r.read_at) r.read_at = at;
        });
        this.unread = 0;
      } catch (_e) {
        this.failed = true;
      }
    },
    linkFor(row) {
      const build = SUBJECT_ROUTES[row.subject_type];
      if (!build) return null;
      const route = build(row.subject_id, row);
      if (!route) return null;
      const known = this.$router && this.$router.hasRoute && this.$router.hasRoute(route.name);
      return known ? route : null;
    },
    openLink(row) {
      const route = this.linkFor(row);
      if (!route) return;
      if (!row.read_at) this.markRead(row);
      this.close();
      this.$router.push(route);
    },
    whenOf(row) {
      return ageLabel(this.now - new Date(row.created_at).getTime());
    },
    toggle() {
      if (this.open) this.close();
      else {
        this.open = true;
        this.fetchRows();
        this.fetchMessageCount();
      }
    },
    close() {
      if (!this.open) return;
      this.open = false;
      this.$nextTick(() => this.$refs.trigger && this.$refs.trigger.focus());
    },
    onKeydown(event) {
      if (event.key === 'Escape') this.close();
    },
    onPointer(event) {
      if (this.open && this.$refs.root && !this.$refs.root.contains(event.target)) this.close();
    },
  },
};
</script>

<style scoped lang="scss">
@import '../../../sass/mixins.generated';

// Mirrors alertBell.vue token for token: same bar, same inverse ink, same
// badge pairing, same paper panel, so the two bells read as one control.
.lw-p-notice-bell {
  position: relative;
  display: flex;
  align-items: center;
  height: 100%;
}

.lw-p-notice-bell__trigger {
  position: relative;
  border: 0;
  background: transparent;
  color: var(--lw-fg-inverse);
  cursor: pointer;
  font: inherit;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 3rem;
  height: 100%;
  padding: 0;

  &:hover {
    background-color: var(--lw-navy-950);
  }

  @include lw-focus-visible;
}

.lw-p-notice-bell__badge {
  position: absolute;
  top: 0.45rem;
  right: 0.55rem;
  min-width: 1.05rem;
  height: 1.05rem;
  padding: 0 0.2rem;
  border-radius: 999px;
  background: var(--lw-reg-600);
  color: var(--lw-fg-inverse);
  font: var(--lw-type-micro);
  line-height: 1.05rem;
  text-align: center;
  box-shadow: 0 0 0 2px var(--lw-bg-chassis);
}

.lw-p-notice-bell__panel {
  position: absolute;
  top: 100%;
  right: 0;
  z-index: var(--lw-z-overlay);
  width: 22rem;
  max-width: calc(100vw - var(--lw-space-4));
  max-height: 70vh;
  overflow-y: auto;
  background: var(--lw-bg-paper);
  color: var(--lw-fg-primary);
  border: 1px solid var(--lw-line-hairline);
  border-radius: var(--lw-radius-md);
  box-shadow: var(--lw-elev-3-shadow);
}

.lw-p-notice-bell__panel-head {
  display: flex;
  align-items: center;
  gap: var(--lw-space-2);
  padding: var(--lw-space-3) var(--lw-space-4);
  border-bottom: 1px solid var(--lw-line-hairline);
}

.lw-p-notice-bell__panel-title {
  flex: 1;
  margin: 0;
  font: var(--lw-type-subhead);
}

.lw-p-notice-bell__close {
  border: 0;
  background: transparent;
  color: var(--lw-fg-secondary);
  cursor: pointer;
  font: inherit;
  padding: var(--lw-space-1);

  @include lw-focus-visible;
}

.lw-p-notice-bell__status {
  margin: 0;
  padding: var(--lw-space-3) var(--lw-space-4);
  font: var(--lw-type-body-sm);
  color: var(--lw-fg-secondary);
}

.lw-p-notice-bell__empty {
  padding-bottom: var(--lw-space-2);
}

.lw-p-notice-bell__empty-title {
  margin: 0;
  padding: var(--lw-space-4) var(--lw-space-4) 0;
  font: var(--lw-type-subhead);
}

.lw-p-notice-bell__list {
  list-style: none;
  margin: 0;
  padding: 0;
}

.lw-p-notice-bell__item {
  padding: var(--lw-space-3) var(--lw-space-4);
  border-bottom: 1px solid var(--lw-line-hairline);

  &:last-child {
    border-bottom: 0;
  }

  // Unread rows carry a left rule in the accent ramp rather than a tinted
  // ground: a tint would be a raw colour literal, which the purity gate
  // refuses.
  &--unread {
    border-left: 3px solid var(--lw-reg-600);
  }
}

.lw-p-notice-bell__messages {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--lw-space-3);
  padding: var(--lw-space-3) var(--lw-space-4);
  border-bottom: 1px solid var(--lw-line-hairline);
  border-left: 3px solid var(--lw-reg-600);
}

.lw-p-notice-bell__subject {
  margin: 0;
  font: var(--lw-type-label);
}

.lw-p-notice-bell__body {
  margin: var(--lw-space-1) 0 0;
  font: var(--lw-type-body-sm);
}

.lw-p-notice-bell__meta {
  margin: var(--lw-space-1) 0 0;
  font: var(--lw-type-caption);
  color: var(--lw-fg-secondary);
}

.lw-p-notice-bell__actions {
  display: flex;
  gap: var(--lw-space-3);
  margin-top: var(--lw-space-2);
}
</style>
